import { FC, useState, useRef, useEffect, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { CloseCrossBtn } from '../../icons/close-cross-btn';
import { CopyLinkIcon } from '../../icons/copy-link-icon';
import { ChevronUpDownIcon } from '../../icons/chevron-up-down';
import { HideFromListIcon } from '../../icons/hide-from-list-icon';
import { EllipsisHorizontalIcon } from '../../icons/ellipsis-horizontal';
import { EllipsisHorizontalActiveIcon } from '../../icons/ellipsis-horizontal-active';
import { toast } from 'react-toastify';
import ModalWrap from '../../common/modal-wrap';
import Button from '../../common/button';
import { ActionMenuPopup } from '../../common/action-menu-popup';

export interface AwaitingParticipant {
  id: string;
  displayName: string;
  avatarColor?: string;
  /** Вход разрешён, но участник ещё не подключился — ждём его появления в комнате */
  isAllowedPending?: boolean;
}

export interface OnMeetingParticipant {
  id: string;
  displayName: string;
  avatarColor?: string;
  isPsychologist?: boolean;
}

export interface GroupParticipantsPanelProps {
  onClose: () => void;
  meetLink: string;
  /** Количество участников, ожидающих подключение (открыли ссылку, но ещё не нажали «Подключиться») */
  awaitingCount?: number;
  awaitingParticipants?: AwaitingParticipant[];
  /** Количество участников уже на встрече (нажали вход и в комнате) */
  onMeetingCount?: number;
  onMeetingParticipants?: OnMeetingParticipant[];
  onAllow?: (id: string) => void;
  /** Скрыть участника из списка «Ожидают подключения» (только UI, участнику ничего не отправляется). */
  onHideFromAwaiting?: (id: string) => void;
  onAllowAll?: () => void;
  onMuteAll?: () => void;
  /** Действия по участнику на встрече (из меню «⋯»). Промис резолвится в true только если участник подтвердил (ack) реальное выполнение, false — если не подтвердил за таймаут. */
  onMuteParticipant?: (id: string) => Promise<boolean>;
  onStopVideoParticipant?: (id: string) => Promise<boolean>;
  onMakeCoOrganizer?: (id: string) => void;
  /** Показывать пункт «Назначить соорганизатором» (только психолог, не соорганизатор) */
  canAssignCoOrganizer?: boolean;
  onRemoveParticipant?: (id: string) => void;
}

export const GroupParticipantsPanel: FC<GroupParticipantsPanelProps> = ({
  onClose,
  meetLink,
  awaitingCount,
  awaitingParticipants = [],
  onMeetingCount,
  onMeetingParticipants = [],
  onAllow,
  onHideFromAwaiting,
  onAllowAll,
  onMuteAll,
  onMuteParticipant,
  onStopVideoParticipant,
  onMakeCoOrganizer,
  canAssignCoOrganizer = false,
  onRemoveParticipant,
}) => {
  const displayAwaitingCount = awaitingCount ?? awaitingParticipants.length;
  const displayOnMeetingCount = onMeetingCount ?? onMeetingParticipants.length;
  const [isAwaitingListExpanded, setIsAwaitingListExpanded] = useState(true);
  const [isOnMeetingListExpanded, setIsOnMeetingListExpanded] = useState(true);
  const [openParticipantMenuId, setOpenParticipantMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ bottom: number; right: number } | null>(null);
  const [participantIdToRemove, setParticipantIdToRemove] = useState<string | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (openParticipantMenuId === null) {
      setMenuPosition(null);
      return;
    }
    const el = menuTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Меню показываем сверху от кнопки (отступ 4px)
    setMenuPosition({ bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right });
  }, [openParticipantMenuId]);

  useEffect(() => {
    if (openParticipantMenuId === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuTriggerRef.current?.contains(target) || menuContentRef.current?.contains(target)) return;
      setOpenParticipantMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openParticipantMenuId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(meetLink).then(
      () => toast.success('Скопировано'),
      () => toast.error('Не удалось скопировать'),
    );
  };

  const moderationFeedbackToast = (message: string, variant: 'success' | 'warning' = 'success') => {
    toast[variant](message, { autoClose: 2800, position: 'top-center' });
  };

  return (
    <div
      className={classNames(
        'w-full h-full min-h-0 flex flex-col bg-[#525458] rounded-[15px] shadow-lg overflow-hidden z-[200]',
      )}
      role="dialog"
      aria-label="Участники группы"
    >
      <div className="flex flex-col flex-1 min-h-0 px-6 pt-4 pb-5 gap-5">
        {/* Заголовок */}
        <div className="flex items-center justify-between gap-4 flex-shrink-0 relative z-10">
          <h2 className="text-white font-bold text-2xl leading-tight">Участники</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-[30px] h-[30px] rounded-full hover:bg-white/10 transition-colors"
            aria-label="Закрыть"
          >
            <CloseCrossBtn width={20} height={20} className="[&_path]:stroke-white" />
          </button>
        </div>

        {/* Поделиться ссылкой — одна строка: подпись, ссылка, кнопка копирования */}
        <div className="flex items-center gap-2 text-white py-2 flex-shrink-0 min-w-0 relative z-10">
          <span className="font-semibold text-base whitespace-nowrap">Поделиться ссылкой</span>
          <span className="text-base truncate min-w-0 flex-1 text-white/95">{meetLink}</span>
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg hover:bg-white/10 transition-colors text-white"
            aria-label="Копировать ссылку"
          >
            <CopyLinkIcon className="w-5 h-5" stroke="white" />
          </button>
        </div>

        {/* Ожидают подключения + На встрече: между секциями 32px */}
        <div className="flex flex-col flex-1 min-h-0 gap-[32px]">
          {/* Ожидают подключения */}
          <div className="flex flex-col gap-4 flex-shrink-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2 flex-shrink-0 relative z-10">
              <span className="text-white font-semibold text-xl">Ожидают подключения</span>
              <div className="flex items-center gap-1">
                <span className="text-white text-base tabular-nums">{displayAwaitingCount}</span>
                <button
                  type="button"
                  onClick={() => setIsAwaitingListExpanded((v) => !v)}
                  className="flex items-center justify-center p-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                  aria-label={isAwaitingListExpanded ? 'Свернуть список' : 'Развернуть список'}
                >
                  <ChevronUpDownIcon direction={isAwaitingListExpanded ? 'up' : 'down'} />
                </button>
              </div>
            </div>
            {isAwaitingListExpanded && (
              <>
                <div className="flex flex-col gap-4 overflow-y-auto min-h-0 max-h-[220px] isolate">
                  {awaitingParticipants.map((p, index) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 relative flex-shrink-0 w-full py-0.5 rounded-lg bg-[#525458]"
                      style={{ zIndex: 100 - index }}
                    >
                      {/* Подложка: только аватар и имя, цвет #636569, радиус 100px */}
                      <div
                        className="flex items-center gap-3 w-fit max-w-full rounded-[100px] py-1 pl-1 pr-4"
                        style={{ backgroundColor: '#636569' }}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: p.avatarColor ?? '#8ED0A7' }}
                        >
                          {(p.displayName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white text-base truncate">{p.displayName}</span>
                      </div>
                      {/* Кнопки: скрыть из списка (только UI), затем Разрешить */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {onHideFromAwaiting && (
                          <button
                            type="button"
                            onClick={() => onHideFromAwaiting(p.id)}
                            className="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer text-[#EAEBEA] hover:bg-white/10 transition-colors"
                            aria-label="Скрыть из списка"
                            title="Скрыть из списка"
                          >
                            <HideFromListIcon width={20} height={20} stroke="#FF5F57" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onAllow?.(p.id)}
                          className="inline-flex items-center justify-center min-h-8 px-3 rounded-full cursor-pointer text-[#EAEBEA] text-base font-normal hover:bg-white/10 transition-colors"
                          title={p.isAllowedPending ? 'Разрешить повторно' : undefined}
                        >
                          {p.isAllowedPending ? 'Ожидает входа…' : 'Разрешить'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {displayAwaitingCount > 0 && (
                  <div className="flex justify-end flex-shrink-0">
                    <button
                      type="button"
                      onClick={onAllowAll}
                      className="flex-none flex flex-row justify-center cursor-pointer items-center h-12 py-[15px] px-4 rounded-[16px] bg-[#ECEFFA] text-[#2C3038] font-medium text-base hover:opacity-90 transition-opacity whitespace-nowrap"
                    >
                      Разрешить всем
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* На встрече */}
          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2 flex-shrink-0 relative z-10">
              <span className="text-white font-semibold text-xl">На встрече</span>
              <div className="flex items-center gap-1">
                <span className="text-white text-base tabular-nums">{displayOnMeetingCount}</span>
                <button
                  type="button"
                  onClick={() => setIsOnMeetingListExpanded((v) => !v)}
                  className="flex items-center justify-center p-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                  aria-label={isOnMeetingListExpanded ? 'Свернуть список' : 'Развернуть список'}
                >
                  <ChevronUpDownIcon direction={isOnMeetingListExpanded ? 'up' : 'down'} />
                </button>
              </div>
            </div>
            {isOnMeetingListExpanded && (
              <>
                <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0 isolate">
                  {onMeetingParticipants.map((p, index) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 relative flex-shrink-0 w-full py-0.5 rounded-lg bg-[#525458]"
                      style={{ zIndex: 100 - index }}
                    >
                      <div
                        className="flex items-center gap-3 w-fit max-w-full rounded-[100px] py-1 pl-1 pr-4 flex-shrink-0 min-w-0"
                        style={{ backgroundColor: '#636569' }}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: p.avatarColor ?? '#8ED0A7' }}
                        >
                          {(p.displayName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white text-base truncate">{p.displayName}</span>
                      </div>
                      {(!p.isPsychologist || canAssignCoOrganizer) && (
                        <button
                          ref={openParticipantMenuId === p.id ? menuTriggerRef : undefined}
                          type="button"
                          className="flex-shrink-0 flex items-center justify-center p-0.5 hover:opacity-80 transition-opacity cursor-pointer"
                          aria-label="Опции участника"
                          aria-expanded={openParticipantMenuId === p.id}
                          onClick={() => setOpenParticipantMenuId((id) => (id === p.id ? null : p.id))}
                        >
                          {openParticipantMenuId === p.id ? (
                            <EllipsisHorizontalActiveIcon width={30} height={30} />
                          ) : (
                            <EllipsisHorizontalIcon />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {displayOnMeetingCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      onMuteAll?.();
                      if (onMuteAll) {
                        moderationFeedbackToast('Микрофон выключен всем участникам');
                      }
                    }}
                    className="w-full h-12 rounded-2xl bg-[#ECEFFA] text-[#2C3038] font-medium text-base hover:opacity-90 transition-opacity flex-shrink-0 cursor-pointer"
                  >
                    Отключить звук всем
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {openParticipantMenuId &&
        menuPosition &&
        (() => {
          const p = onMeetingParticipants.find((pr) => pr.id === openParticipantMenuId);
          if (!p) return null;

          const closeMenu = () => setOpenParticipantMenuId(null);

          const participantLabel = (p.displayName || 'Участник').trim();

          const menuItems = [
            {
              label: 'Выключить звук',
              onClick: () => {
                closeMenu();
                onMuteParticipant?.(p.id).then((ok) => {
                  moderationFeedbackToast(
                    ok
                      ? `Микрофон успешно выключен: ${participantLabel}`
                      : `Не удалось подтвердить отключение микрофона: ${participantLabel}`,
                    ok ? 'success' : 'warning',
                  );
                });
              },
            },
            {
              label: 'Остановить видео',
              onClick: () => {
                closeMenu();
                onStopVideoParticipant?.(p.id).then((ok) => {
                  moderationFeedbackToast(
                    ok
                      ? `Видео успешно остановлено: ${participantLabel}`
                      : `Не удалось подтвердить остановку видео: ${participantLabel}`,
                    ok ? 'success' : 'warning',
                  );
                });
              },
            },
            ...(canAssignCoOrganizer
              ? [
                  {
                    label: 'Назначить соорганизатором',
                    onClick: () => {
                      onMakeCoOrganizer?.(p.id);
                      closeMenu();
                      if (onMakeCoOrganizer) {
                        moderationFeedbackToast(`Назначен соорганизатором: ${participantLabel}`);
                      }
                    },
                  },
                ]
              : []),
            {
              label: 'Удалить участника',
              onClick: () => {
                closeMenu();
                setParticipantIdToRemove(p.id);
              },
              variant: 'danger' as const,
            },
          ];

          return <ActionMenuPopup items={menuItems} position={menuPosition} contentRef={menuContentRef} />;
        })()}
      {/* Модальное окно подтверждения удаления участника */}
      <ModalWrap isShow={!!participantIdToRemove}>
        {participantIdToRemove && (
          <div
            className="think-scroll flex flex-col gap-6 w-full max-w-[90%] sm:max-w-[60%] md:max-w-[560px] h-auto rounded-[20px] p-6 lg:p-10 bg-white shadow-shadowPopup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-participant-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex justify-between items-start">
              <h2 id="remove-participant-title" className="font-comfortaa text-lg lg:text-2xl text-[#111026] font-bold">
                Удалить участника со встречи?
              </h2>
              <button
                type="button"
                className="cursor-pointer shrink-0"
                aria-label="Закрыть"
                onClick={() => setParticipantIdToRemove(null)}
              >
                <CloseCrossBtn width={20} height={20} />
              </button>
            </div>
            <p className="text-sm md:text-base font-bold text-[#585858]">
              После удаления участник сможет присоединиться к этой встрече снова.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                type="grey"
                className="!w-max px-4 py-2 !text-sm md:!text-base"
                onClick={() => setParticipantIdToRemove(null)}
              >
                Отмена
              </Button>
              <Button
                type="black"
                className="!w-max px-4 py-2 !text-sm md:!text-base"
                onClick={() => {
                  onRemoveParticipant?.(participantIdToRemove);
                  setParticipantIdToRemove(null);
                }}
              >
                Удалить
              </Button>
            </div>
          </div>
        )}
      </ModalWrap>
    </div>
  );
};
